import { useMutation, useQueryClient } from '@tanstack/react-query';
import { post } from '@/shared/api/api-client';
import {
  formatLocalDateOnlyForWire,
  formatLocalDatetimeForWire,
} from '@/shared/lib/date-format';
import type { CorporateList } from '@/types/patient-api';

/**
 * POST /api/Patients/PatientCorporate
 *
 * **Why POST for an edit flow?** Same list-replace upsert pattern as
 * `useSaveHealthInsurance`. Body is the patient's COMPLETE corporate
 * array — existing rows (with `corporateId`), new rows (no id), and
 * soft-deleted rows (`isDeleted: true`) all ship together; backend
 * reconciles the persisted set. Flutter `patient_data_source_impl.dart:400-413`.
 *
 * Wire-shape rules (mirrors the insurance save patterns; pending live
 * dev-curl verification specifically for the corporate endpoint):
 *
 *   - **Dates ship without the `Z` suffix.** .NET local-DateTime form.
 *     `toWireDate()` normalises to `YYYY-MM-DDTHH:mm:ss.sss` off the LOCAL
 *     clock (see PROD-1778 note on the helper).
 *   - **`submittedDate` is set to now on every save.**
 *   - **Soft-deleted-but-never-saved rows** are stripped client-side
 *     (Flutter `:404` `removeWhere data.isDeleted && data.corporateId == null`).
 *   - **`isReviewed` defaults to true on the wire** (matches insurance).
 *   - **`billingGroups` ship `{billingGroupId, category, value}`** — NO
 *     `maximumCopayment` (insurance has it; corporate does not). Confirmed
 *     against the 2026-06-05 PatientCorporate dev curl, whose request body
 *     carried `"billingGroups":[{"billingGroupId":"…","category":null,
 *     "value":20}]`. Earlier this hook shipped an empty array
 *     unconditionally — the discount breakdown never persisted (F-PAT-243).
 *
 * Cache: invalidates the patient query since corporate agreements are
 * carried inside `patient.corporateAgreementResponse`. No separate
 * `corporate-agreements` query — same reason no GET endpoint exists.
 */

interface SaveCorporateAgreementsArgs {
  patientId: string;
  corporateList: CorporateList[];
}

/**
 * Normalise `eligiblityStartDate` / `expirationDate` for the wire. Null for
 * falsy input.
 *
 * PROD-1778: was `iso.slice(0, -1)` on `Z`-suffixed input — a FALSE safeguard.
 * `toISOString()` converts the instant to UTC before the slice, so removing the
 * `Z` bakes the offset in instead of removing it, and the .NET binder stores the
 * literal. Both fields are CALENDAR DAYS, so the day-preserving normaliser is
 * the correct one (see the sibling `useSaveHealthInsurance` hook, which this
 * file was copied from).
 */
function toWireDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  return formatLocalDateOnlyForWire(iso) || null;
}

/**
 * `submittedDate` stamp. PROD-1778: previously built from `getUTC*` getters, so
 * the audit timestamp shipped UTC wall-clock 4h behind the operator's actual
 * time in UAE. Reads the local clock now, matching Flutter `DateTime.now()`.
 */
function nowLocalIso(): string {
  return formatLocalDatetimeForWire(new Date());
}

/**
 * Wire shape for `corporateProvider` — mirrors Flutter `PayerDetails`.
 * `CorporateList.corporateProvider` is `PatientPayerDetails`-typed
 * (`patient-api.ts`), which carries the same field set; this helper
 * just normalises nulls / empty-strings to match the verified
 * 2026-05-14 PatientCorporate curl shape.
 */
interface WireCorporateProvider {
  id: string | null;
  imageUrl: string | null;
  payerName: string | null;
  payerCompanyId: string | null;
  address: string | null;
  office: string | null;
  website: string | null;
  email: string | null;
  payerAgreementName: string | null;
  payerAgreementId: string | null;
  phoneNumber: string | null;
  note: string | null;
  relatedTpa: unknown[];
}

function toWireProvider(
  p: CorporateList['corporateProvider'],
): WireCorporateProvider | null {
  if (!p) return null;
  return {
    id: p.id ?? null,
    imageUrl: p.imageUrl?.trim() ? p.imageUrl : '',
    payerName: p.payerName ?? null,
    payerCompanyId: p.payerCompanyId ?? null,
    address: p.address ?? '',
    office: p.office ?? '',
    website: p.website ?? '',
    email: p.email ?? '',
    payerAgreementName: p.payerAgreementName ?? null,
    payerAgreementId: p.payerAgreementId ?? null,
    phoneNumber: p.phoneNumber ?? '',
    note: p.note ?? '',
    relatedTpa: p.relatedTpa ?? [],
  };
}

function toWireRow(row: CorporateList, patientId: string) {
  return {
    corporateId: row.corporateId ?? null,
    patientId: row.patientId ?? patientId,
    agreementId: row.agreementId ?? null,
    agreementName: row.agreementName ?? null,
    corporateProviderId:
      row.corporateProviderId ?? row.corporateProvider?.id ?? null,
    corporateProvider: toWireProvider(row.corporateProvider),
    policyNumber: row.policyNumber ?? '',
    // Legacy empty-string base64 fields — backend's model binder
    // requires them present. Always ship "" (verified via 2026-05-14
    // curl; matches the insurance save hook pattern).
    frontPhoto: '',
    backPhoto: '',
    frontPhotoUrl: row.frontPhotoUrl ?? null,
    backPhotoUrl: row.backPhotoUrl ?? null,
    eligiblityStartDate: toWireDate(row.eligiblityStartDate),
    expirationDate: toWireDate(row.expirationDate),
    submittedDate: nowLocalIso(),
    isReviewed: row.isReviewed ?? true,
    isDeleted: !!row.isDeleted,
    isVerified: !!row.isVerified,
    discountType: row.discountType ?? '',
    // Corporate ships {billingGroupId, category, value} ONLY — no
    // maximumCopayment (that field is insurance-only). Empty array when
    // no discount breakdown is configured. 2026-06-05 PatientCorporate curl.
    //
    // Zero-value handling (Samer review PR #495): we do NOT filter out
    // value:0 rows here. A newly-selected group with no value can't reach
    // this point — the editor gates Done on `discountType && every group
    // has a truthy value` (corporate-agreement-row.tsx `discountValid`),
    // mirroring Flutter's "Please Enter value for selected billing groups"
    // save guard. Filtering at the hook would instead silently DROP a
    // legitimate server-loaded row that genuinely carries 0, breaking the
    // round-trip. Validation belongs at the form; the hook is a faithful
    // serializer.
    billingGroups: (row.billingGroups ?? []).map((g) => ({
      billingGroupId: g.billingGroupId ?? null,
      category: g.category ?? null,
      value: g.value ?? 0,
    })),
  };
}

export function useSaveCorporateAgreements() {
  const queryClient = useQueryClient();

  return useMutation<unknown, Error, SaveCorporateAgreementsArgs>({
    mutationFn: ({ patientId, corporateList }) => {
      // Drop unsaved rows that the user added then deleted — Flutter
      // does the same client-side filter before send.
      const cleaned = corporateList.filter(
        (row) => !(row.isDeleted && !row.corporateId),
      );
      const body = cleaned.map((row) => toWireRow(row, patientId));
      return post<unknown>('/api/Patients/PatientCorporate', body);
    },
    onSuccess: () => {
      // Corporate list comes back via the patient body — invalidate the
      // patient query so the section refreshes from the next fetch.
      queryClient.invalidateQueries({ queryKey: ['patient'] });
    },
  });
}
